import { AppTheme, ThemeColors } from '@settings/themes/types';

import { makeTransparent } from './transparentColor';

export type { AppTheme, ThemeColors };

const clampColorChannel = (value: number): number => Math.max(0, Math.min(255, Math.round(value)));

const parseHexColor = (hexColor: string): [number, number, number] | null => {
  const normalizedHex = hexColor.replace('#', '');
  if (!/^([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(normalizedHex)) {
    return null;
  }

  const fullHex = normalizedHex.length === 3
    ? normalizedHex.split('').map((value) => `${value}${value}`).join('')
    : normalizedHex;

  return [
    parseInt(fullHex.slice(0, 2), 16),
    parseInt(fullHex.slice(2, 4), 16),
    parseInt(fullHex.slice(4, 6), 16),
  ];
};

const createLightThemeAccent = (hexColor: string, mixRatio = 0.82): string => {
  const rgb = parseHexColor(hexColor);
  if (!rgb) {
    return hexColor;
  }

  const [red, green, blue] = rgb;
  const mixChannel = (value: number) => clampColorChannel(value + (255 - value) * mixRatio);

  return `rgb(${mixChannel(red)}, ${mixChannel(green)}, ${mixChannel(blue)})`;
};

/**
 * Applies a theme by setting CSS variables on the document root
 * @param theme The theme configuration to apply
 */
export function applyTheme(theme: AppTheme): void {
  // Apply colors
  Object.entries(theme.colors).forEach(([key, value]) => {
    document.documentElement.style.setProperty(`--color-${key}`, value);
  });
  document.documentElement.style.setProperty('--color-primary-soft', createLightThemeAccent(theme.colors.primary));
  document.documentElement.style.setProperty('--color-primary-soft-glow', makeTransparent(theme.colors.primary, 0.4));

  // Apply background image
  document.documentElement.style.setProperty('--bg-image', `url(${theme.backgroundImageURL})`);

  // Apply grayscale filter (if enabled)
  const grayscaleValue = theme.grayscaleBackground ? '1' : '0';
  document.documentElement.style.setProperty('--bg-grayscale', grayscaleValue);

  // Apply gradient overlay (if enabled)
  if (theme.backgroundGradient?.enabled && theme.backgroundGradient.colors.length > 0) {
    const gradient = `linear-gradient(${theme.backgroundGradient.angle}deg, ${theme.backgroundGradient.colors.join(', ')})`;
    document.documentElement.style.setProperty('--bg-gradient', gradient);
    document.documentElement.style.setProperty('--bg-gradient-opacity', '1');
  } else {
    document.documentElement.style.setProperty('--bg-gradient-opacity', '0');
  }
}

